import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, CheckCircle, AlertCircle, Smartphone, Key } from "lucide-react";
import { confirmSignIn } from "aws-amplify/auth";
import { generateQRCode } from "@/services/mfaService";

const CognitoMFASetup = ({ onComplete, onCancel, totpSetupDetails, userEmail }) => {
  const [step, setStep] = useState(1); // 1: Show QR code, 2: Verify code, 3: Complete
  const [secret, setSecret] = useState("");
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (step === 1 && totpSetupDetails) {
      setupTOTP();
    }
  }, [step, totpSetupDetails]);

  const setupTOTP = () => {
    try {
      setLoading(true);
      setError("");
      
      // Use the shared secret from Cognito
      const sharedSecret = totpSetupDetails.sharedSecret;
      setSecret(sharedSecret);
      
      // Generate QR code with proper issuer and account name
      const qrData = generateQRCode(sharedSecret, userEmail || "user@example.com");
      setQrCodeUrl(qrData.qrCodeUrl);
      
      setStep(2);
    } catch (error) {
      setError(error.message || "Failed to setup TOTP");
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError("Please enter a valid 6-digit code");
      return;
    }

    try {
      setLoading(true);
      setError("");
      
      // Use Cognito's confirmSignIn with TOTP setup
      const { isSignedIn } = await confirmSignIn({
        challengeResponse: verificationCode
      });

      if (isSignedIn) {
        setSuccess("MFA setup completed successfully!");
        setStep(3);
        
        // Immediately call onComplete to start redirect process
        onComplete?.();
      } else {
        setError("MFA setup failed. Please try again.");
      }
    } catch (error) {
      setError(error.message || "Failed to verify code");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    onCancel?.();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Smartphone className="h-5 w-5" />
              Setup Multi-Factor Authentication
            </CardTitle>
            <CardDescription>
              Secure your account with Time-based One-Time Password (TOTP)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {step === 1 && (
              <div className="text-center">
                <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
                <p>Setting up your MFA secret...</p>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-6">
                <div className="text-center">
                  <h3 className="text-lg font-medium mb-4">Scan QR Code</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Use your authenticator app to scan this QR code:
                  </p>
                  
                  {qrCodeUrl && (
                    <div className="flex justify-center mb-4">
                      <img 
                        src={qrCodeUrl} 
                        alt="MFA QR Code" 
                        className="border rounded-lg"
                      />
                    </div>
                  )}
                  
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <Label className="text-sm font-medium">Manual Entry Key:</Label>
                    <div className="mt-2 p-2 bg-white border rounded font-mono text-sm break-all">
                      {secret}
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="verification-code">Enter 6-digit code from your app:</Label>
                    <Input
                      id="verification-code"
                      type="text"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      placeholder="123456"
                      maxLength={6}
                      className="text-center text-lg tracking-widest"
                    />
                  </div>
                  
                  <Button 
                    onClick={verifyCode} 
                    disabled={loading || verificationCode.length !== 6}
                    className="w-full"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      "Verify & Complete Setup"
                    )}
                  </Button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="text-center space-y-4">
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto" />
                <h3 className="text-lg font-medium text-green-800">MFA Setup Complete!</h3>
                <p className="text-sm text-gray-600">
                  Your account is now protected with multi-factor authentication.
                </p>
              </div>
            )}

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {success && (
              <Alert>
                <CheckCircle className="h-4 w-4" />
                <AlertDescription>{success}</AlertDescription>
              </Alert>
            )}

            <div className="flex gap-3">
              {step < 3 && (
                <Button variant="outline" onClick={handleCancel} className="flex-1">
                  Cancel
                </Button>
              )}
              {step === 3 && (
                <div className="text-center">
                  <div className="flex items-center justify-center space-x-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Redirecting to dashboard...</span>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="text-center text-sm text-gray-500">
          <p>Popular authenticator apps:</p>
          <p>Google Authenticator, Microsoft Authenticator, Authy, 1Password</p>
        </div>
      </div>
    </div>
  );
};

export default CognitoMFASetup;
